import React, { useState } from "react";
import { ChevronsUpDown, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useGetContactsQuery } from "@/store/rest_api";
import { getContactDisplayName } from "@/util/string";
import { ContactType } from "@/enum/ContactType";

interface RecipientPickerProps {
  value: string;
  onChange: (phoneNumber: string) => void;
}

const SEARCH_SIZE = 20;
const MIN_NUMBER_DIGITS = 7;

function looksLikeNumber(text: string): boolean {
  return text.replace(/\D/g, "").length >= MIN_NUMBER_DIGITS;
}

export default function RecipientPicker({ value, onChange }: RecipientPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const { data, isFetching } = useGetContactsQuery({ size: SEARCH_SIZE, q: search.trim() || undefined });

  const contacts = data?.items ?? [];
  const typed = search.trim();

  const choose = (phoneNumber: string, name: string | null) => {
    onChange(phoneNumber);
    setSelectedName(name);
    setSearch("");
    setOpen(false);
  };

  const triggerLabel = () => {
    if (!value) {
      return "Search a contact or type a number";
    }
    return selectedName ? `${selectedName} (${value})` : value;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          <span className={value ? "truncate" : "truncate text-muted-foreground"}>{triggerLabel()}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Name or phone number" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              {isFetching ? "Searching..." : typed ? "No contacts found." : "Type a name or a phone number."}
            </CommandEmpty>
            {looksLikeNumber(typed) && (
              <CommandGroup>
                <CommandItem value={`number:${typed}`} onSelect={() => choose(typed, null)}>
                  <Phone className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">Message {typed}</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {contacts.map((contact) =>
                (contact.phones ?? []).map((phone) => {
                  const phoneNumber = phone.phoneNumberE164 || phone.phoneNumber;
                  if (!phoneNumber) {
                    return null;
                  }
                  const name = getContactDisplayName(contact) || "Unnamed";
                  return (
                    <CommandItem
                      key={`${contact.id}:${phoneNumber}`}
                      value={`${contact.id}:${phoneNumber}`}
                      onSelect={() => choose(phoneNumber, name)}
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">
                          {name}
                          {contact.type === ContactType.LEAD && (
                            <span className="ml-1.5 text-xs text-muted-foreground">Lead</span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {phone.phoneType ? `${phone.phoneType} ${phoneNumber}` : phoneNumber}
                        </span>
                      </div>
                    </CommandItem>
                  );
                }),
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
